import React from 'react';
import { useHistory } from 'react-router-dom';
import { Footer, Footnote } from '@vwfs-bronson/bronson-react';
import { LegalFooterCMS, LegalFooterLinkCMS } from '@cp-shared-19/apis';
import { Override } from '@cp-shared-19/common-utilities';

export type LegalFooterProps = Override<
    LegalFooterCMS,
    {
        legalText?: string | React.ReactNode;
        metaSectionLabel?: string;
        trackingFunction?: (label: string, url: string) => void;
        footerRole?: string;
    }
>;

function isExternalLink(url: string): boolean {
    return /^(https?:\/\/|mailto:|tel:)/.test(url);
}

/**
 * A legal footer consolidating all necessary legal footprint
 */
export const LegalFooter: React.FC<LegalFooterProps> = ({
    links,
    legalText,
    copyrightText,
    footnotes,
    metaSectionLabel,
    footerAriaLabel,
    trackingFunction,
    footerRole,
}) => {
    const history = useHistory();

    return (
        <Footer {...(footerRole && { role: footerRole })} {...(footerAriaLabel && { 'aria-label': footerAriaLabel })}>
            <Footer.ModuleMetaNav copyrightText={copyrightText} metaSectionLabel={metaSectionLabel || ''}>
                {links.map(({ text, ariaLabel, title, url = '', externalUrl }: LegalFooterLinkCMS) => {
                    const linkTarget = externalUrl || url;
                    const isExternal = !!externalUrl || isExternalLink(linkTarget);

                    const handleClick = (e: React.MouseEvent) => {
                        if (trackingFunction) trackingFunction(text, linkTarget);

                        if (!isExternal && linkTarget.startsWith('/')) {
                            e.preventDefault();
                            history.push(linkTarget);
                        }
                    };

                    return (
                        <Footer.ModuleMetaNav.Item
                            key={text}
                            href={linkTarget}
                            normalLink={isExternal}
                            linkProps={{
                                ...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
                                'aria-label': ariaLabel ?? text,
                                title: title ?? text,
                                onClick: handleClick,
                            }}
                        >
                            {text}
                        </Footer.ModuleMetaNav.Item>
                    );
                })}
            </Footer.ModuleMetaNav>

            {legalText || footnotes ? (
                <Footer.ModuleFootnotes footerText={legalText}>
                    {footnotes && (
                        <Footnote numbered>
                            {footnotes.map(footnote => (
                                <Footnote.Item key={footnote}>{footnote}</Footnote.Item>
                            ))}
                        </Footnote>
                    )}
                </Footer.ModuleFootnotes>
            ) : null}
        </Footer>
    );
};
